import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import MonthlyPerformance from "../../components/MonthlyPerformance";
import WeeklyPerformance from "../../components/WeeklyPerformance";
import TraderNavTabs from "../../components/TraderNavTabs";

const formatDuration = (openTime, closeTime) => {
  if (!openTime || !closeTime) return '-';
  const open = new Date(openTime);
  const close = new Date(closeTime);
  const diffMs = close - open;
  
  if (diffMs < 0) return '-';
  
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    const remainHours = hours % 24;
    return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remainMins = minutes % 60;
    return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

const TraderPerformancePage = () => {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("account_id");
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradesLoading, setTradesLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchData = async () => {
      if (!accountId) {
        setLoading(false);
        return;
      }
      try {
        const accountsData = await api.getAccounts();
        const currentAccount = accountsData.find(a =>
          (a.ID || a.id) === accountId
        );
        setAccount(currentAccount);
        await fetchTrades();
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accountId]);

  const fetchTrades = async () => {
    if (!accountId) return;
    try {
      setTradesLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await fetch(
        `http://45.32.118.117:8090/api/trader/trades?account_id=${accountId}&limit=1000`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const closedTrades = (data.trades || [])
        .filter(t => t.close_time)
        .sort((a, b) => new Date(b.close_time) - new Date(a.close_time));
      setTrades(closedTrades);
    } catch (err) {
      console.error('Failed to fetch trades:', err);
      setTrades([]);
    } finally {
      setTradesLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Performance Analysis</h1>
        </div>
        <div className="card">
          <p className="card-meta">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (!accountId || !account) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Performance Analysis</h1>
        </div>
        <div className="card">
          <p className="card-meta">Please select an account from the dashboard</p>
          <Link to="/trader" className="btn-primary" style={{marginTop: 16, display: 'inline-block'}}>
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(trades.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedTrades = trades.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Performance Analysis</h1>
          <p className="page-subtitle">
            {account.nickname || account.account_number} • {account.broker}
          </p>
        </div>
      </div>

      {/* Nav Tabs */}
      <TraderNavTabs accountId={accountId} />

      {/* Monthly Performance */}
      <MonthlyPerformance accountId={accountId} />

      {/* Weekly Performance */}
      <WeeklyPerformance accountId={accountId} />

      {/* Closed Trades Table */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16
        }}>
          <h2 className="card-title">
            Closed Trades
            <span style={{ 
              marginLeft: 8, 
              fontSize: 13, 
              color: '#6b7280', 
              fontWeight: 400 
            }}>
              ({trades.length} total)
            </span>
          </h2>
          
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#6b7280' }}>Show:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 12px',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: 6,
                color: '#fff',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {tradesLoading ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
            Loading trades...
          </div>
        ) : trades.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
            No closed trades found
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #333', textAlign: 'left' }}>
                    {['Symbol','Type','Lots','Open Price','Close Price','Open Date','Close Date','Duration','Profit'].map(h => (
                      <th key={h} style={{ padding: '12px 8px', color: '#9ca3af', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedTrades.map((trade, idx) => {
                    const profit = parseFloat(trade.profit || 0) +
                                   parseFloat(trade.swap || 0) +
                                   parseFloat(trade.commission || 0);
                    const isProfit = profit >= 0;

                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #222' }}>
                        <td style={{ padding: '10px 8px', color: '#fff', fontWeight: 500 }}>
                          {trade.symbol}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            background: trade.type === 'buy' ? '#10b98120' : '#ef444420',
                            color: trade.type === 'buy' ? '#10b981' : '#ef4444',
                            textTransform: 'uppercase'
                          }}>
                            {trade.type}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', color: '#9ca3af' }}>
                          {parseFloat(trade.lots || 0).toFixed(2)}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#9ca3af' }}>
                          {parseFloat(trade.open_price || 0).toFixed(5)}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#9ca3af' }}>
                          {parseFloat(trade.close_price || 0).toFixed(5)}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {formatDate(trade.open_time)}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {formatDate(trade.close_time)}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {formatDuration(trade.open_time, trade.close_time)}
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          color: isProfit ? '#10b981' : '#ef4444',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          {isProfit ? '+' : ''}${profit.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid #333',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 14,
              color: '#9ca3af'
            }}>
              <div>
                Showing {startIndex + 1}–{Math.min(startIndex + rowsPerPage, trades.length)} of {trades.length} trades
              </div>
              
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 10px',
                    background: 'transparent',
                    border: '1px solid #333',
                    borderRadius: 6,
                    color: currentPage === 1 ? '#444' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: 13
                  }}
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    background: currentPage === 1 ? 'transparent' : '#3b82f6',
                    border: '1px solid #333',
                    borderRadius: 6,
                    color: currentPage === 1 ? '#444' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: 13
                  }}
                >
                  Prev
                </button>
                
                <span style={{ padding: '0 8px', color: '#fff' }}>
                  {currentPage} / {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    background: currentPage === totalPages ? 'transparent' : '#3b82f6',
                    border: '1px solid #333',
                    borderRadius: 6,
                    color: currentPage === totalPages ? '#444' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: 13
                  }}
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 10px',
                    background: 'transparent',
                    border: '1px solid #333',
                    borderRadius: 6,
                    color: currentPage === totalPages ? '#444' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: 13
                  }}
                >
                  »
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TraderPerformancePage;
